import { RefObject, useEffect, useRef } from "react";

// NOTE: Taken from github.com/Eliav2/react-responsive-overflow-list but not added as dependency because we
// only use a subset of functionality

type ResizeObserverDimensions = {
    width: number;
    height: number;
    contentWidth: number;
    contentHeight: number;
};

export function useResizeObserver<T extends HTMLElement | null>(
    elementRef: RefObject<T>,
    callback: (dimensions: ResizeObserverDimensions) => void
): void {
    const resizeObserverRef = useRef<ResizeObserver | null>(null);
    const callbackRef = useRef(callback);
    callbackRef.current = callback;

    // Initialize resize observer
    useEffect(() => {
        if (!elementRef.current) return;

        // Create resize observer
        resizeObserverRef.current = new ResizeObserver((entries) => {
            if (entries[0]) {
                const entry = entries[0];
                const newDimensions = {
                    width: entry.borderBoxSize[0]?.inlineSize ?? entry.target.clientWidth,
                    height: entry.borderBoxSize[0]?.blockSize ?? entry.target.clientHeight,
                    contentWidth: entry.contentRect.width,
                    contentHeight: entry.contentRect.height,
                };
                callbackRef.current(newDimensions);
            }
        });

        resizeObserverRef.current.observe(elementRef.current);

        return () => {
            if (resizeObserverRef.current) {
                resizeObserverRef.current.disconnect();
                resizeObserverRef.current = null;
            }
        };
    }, [elementRef]);
}
